import {
  Dispatch, SetStateAction, useEffect, useState,
} from 'react'
import { useLocation } from 'react-router-dom'
// @ts-expect-error TS7016
import { HashLink } from 'react-router-hash-link'

import { faCircleRight } from '@fortawesome/pro-light-svg-icons'

import { hyphenCaseToSnakeCase, snakeToTitleCase } from '@domain-group/javascript-utils'

import useSelector from '@hooks/useSelector'

import AgencyImage from '@sections/Admin/packs/SystemManager/components/NestedNavigation/assets/Agency.svg'
import ProductImage from '@sections/Admin/packs/SystemManager/components/NestedNavigation/assets/Product.svg'
import ProviderImage from '@sections/Admin/packs/SystemManager/components/NestedNavigation/assets/Provider.svg'
import PublicationImage from '@sections/Admin/packs/SystemManager/components/NestedNavigation/assets/Publication.svg'
import { systemManagerIcons } from '@functions/navigation'

const scrollWithOffset = (el: HTMLElement) => {
  const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset
  const yOffset = -172
  window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' })
}

const mapItems = (idsArray: string[], pathname: string) => idsArray.map((id) => {
  const title = id.replace('realhub-nav-', '')

  return ({
    // @ts-expect-error TS7053
    icon: systemManagerIcons[title] || faCircleRight,
    key: id,
    linkProps: {
      scroll: (el: HTMLElement) => scrollWithOffset(el),
      smooth: true,
      to: `${pathname}#${id}`,
    },
    linkTag: HashLink,
    title: snakeToTitleCase(hyphenCaseToSnakeCase(title)),
    visible: true,
  })
})

const generateNavigationItems = (idsArray: string[], pathname: string) => {
  if (idsArray.at(-1) === 'realhub-nav-activities'){
    const activityItem = idsArray.pop()

    return {
      sections: [
        {
          key: 'hashLinks',
          title: 'SECTIONS',
          visible: true,
          items: mapItems(idsArray, pathname),
        },
        {
          key: 'activities',
          title: 'ACTIVITIES',
          visible: true,
          // @ts-expect-error TS2322
          items: mapItems([activityItem], pathname),
        },
      ],
    }
  }

  return {
    sections: [
      {
        key: 'hashLinks',
        title: 'SECTIONS',
        visible: true,
        items: mapItems(idsArray, pathname),
      },
    ],
  }
}

type NavigationItems = ReturnType<typeof generateNavigationItems>

const allowedPages = [
  { regex: /\/agencies\/(\d+)\/edit\/relations/, image: AgencyImage, reduxKey: 'agencies' },
  { regex: /\/products\/(\d+)\/edit\/relations/, image: ProductImage, reduxKey: 'serviceProducts' },
  { regex: /\/providers\/(\d+)\/edit\/relations/, image: ProviderImage, reduxKey: 'providers' },
  { regex: /\/publications\/(\d+)\/edit\/relations/, image: PublicationImage, reduxKey: 'publications' },
]

const setNavItems = (pathname: string, setNavigationItems: Dispatch<SetStateAction<NavigationItems | undefined>>) => {
  const nodeList = document.querySelectorAll('[id^="realhub-nav-"]')
  // @ts-expect-error TS2802
  const ids = [...nodeList].map(node => node.id)

  const navItems = generateNavigationItems(ids, pathname)
  setNavigationItems(navItems)
}

function useSystemManagerNestedNavigation() {
  const { pathname } = useLocation()
  const [navigationItems, setNavigationItems] = useState<NavigationItems>()

  const allowedPage = allowedPages.find(({ regex }) => pathname.match(regex))
  const shouldUseNestedNavigation = !!allowedPage

  const { image, reduxKey, regex } = allowedPage || {}

  // @ts-expect-error TS2461
  const [_, id] = regex ? pathname.match(regex) : []
  // @ts-expect-error TS2538
  const entities = useSelector(reduxState => reduxState.entities[reduxKey]) || {}
  const entity = entities[id] || {}

  useEffect(() => {
    if (shouldUseNestedNavigation) setNavItems(pathname, setNavigationItems)

    // Some modules are conditional
    // If you refresh the page we need to wait for all entities to load
    const timer = setTimeout(() => {
      if (shouldUseNestedNavigation){
        setNavItems(pathname, setNavigationItems)
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [pathname])

  return {
    entity,
    image,
    navigationItems,
    shouldUseNestedNavigation,
  }
}

export default useSystemManagerNestedNavigation
